import React, { ReactNode, useCallback, useContext } from 'react';
import { DistributiveOmit } from '@direct-frontend/stdlib';
import { isMobile } from '@direct-frontend/platform-code';

import { IconArrowDown, IconArrowRight } from '../../../icons';
import { CheckboxProps, CheckboxSize, Checkbox } from '../../Checkbox';
import { HStack, VStack } from '../../Stack';
import { ListItem, ListItemProps } from '../ListItem';
import { ListContext } from '../utils';
import { ClickableIcon } from '../../ClickableIcon';
import { cn } from '../../../lib/css/className/className';
import { useInternalVerticalAlignWithSkin } from '../../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { TextPresetWithSkin } from '../../TextGeneric/types';
import { TextGenericWithSkin } from '../../TextGeneric/TextGenericWithSkin';
import { useTheme } from '../../../lib/theme';
import { ListSize } from '../../ListBoxBase/types';
import './ListTreeItem.css';

const cls = cn('ListTreeItem');

export type ListTreeItemProps = DistributiveOmit<
    ListItemProps,
    'children' | 'textValue' | 'iconLeft' | 'addonLeft' | 'onClick' | 'id' | 'href' | 'target' | 'routerOptions'
> &
    Pick<CheckboxProps, 'isSelected' | 'onChange' | 'isIndeterminate'> & {
        /** Содержимое элемента дерева */
        children: ReactNode;

        /**
         * Уровень в дереве.
         * При положительных значениях рисует стрелку и увеличивает отступ
         * 0 - без отступов
         * >0 - все блоки выровнены горизонтально, вне зависимости от наличия стрелочки по уровню.
         * Все блоки с одинаковым уровнем будут на одной вертикали
         */
        level: number;

        /**
         * Признак раскрытого элемента. У раскрытого элемента стрелка повернута вниз
         */
        isExpanded?: boolean;

        /**
         * Функция, вызываемая при клике на стрелку
         * @param isExpanded признак раскрытия элемента
         * @returns void
         */
        onExpandChange?: (isExpanded: boolean) => void;

        /**
         * Признак наличия стрелки раскрытия. Если false, то стрелки не будет
         */
        hasExpandIcon?: boolean;

        /**
         * Признак наличия чекбокса. Если false, то чекбокса не будет
         * @default true
         */
        hasCheckbox?: boolean;
    };

const checkboxSizeMap: Record<ListSize, CheckboxSize> = {
    xs: 'xs',
    s: 'm',
    m: 'm',
} as const;

const checkboxSizeToIconHeight: Record<ListSize, string> = {
    xs: 'var(--dc-size-skin-xs-icon-icon-container)',
    m: 'var(--dc-size-skin-m-icon-icon-container)',
    s: 'var(--dc-size-skin-m-icon-icon-container)',
} as const;
/**
 * Компонент для отображения элемента дерева
 */
export const ListTreeItem = function ListTreeItem(props: ListTreeItemProps) {
    const {
        level,
        isSelected = false,
        isIndeterminate,
        hasExpandIcon,
        hasCheckbox = true,
        onChange,
        isDisabled,
        isExpanded = false,
        onExpandChange,
        'data-testid': dataTestId = 'ListTreeItem',
        children,
        caption,
        help,
        isTruncated = false,
        ...restProps
    } = props;
    const { size } = useContext(ListContext);

    const onIconClick = useCallback(() => {
        onExpandChange?.(!isExpanded);
    }, [isExpanded, onExpandChange]);

    const onItemClick = useCallback(() => {
        onChange?.(!isSelected);
    }, [isSelected, onChange]);

    const Icon = isExpanded ? IconArrowDown : IconArrowRight;

    const textSize: TextPresetWithSkin = 'current-main + current-caption';
    const { addonClasses } = useInternalVerticalAlignWithSkin({
        textSize,
        addonType: 'universal',
    });
    const skinSizeTokens = useTheme().sizeTokens[size];

    // Размер иконки не подходит под размер из скина, поэтому тут исключение DIRECT-238538
    const iconSize = size === 's' ? '20' : skinSizeTokens.iconSize;

    const gapBetweenText = 8;
    const gapBetweenIcons = 8;
    const iconSizeNumber = Number(iconSize);
    const countOfSteps = level - 1;

    function countMarginLevel() {
        if (countOfSteps < 0) {
            return 0;
        }

        // Каждый подъем это иконка + отступ от иконки до текста
        return hasExpandIcon
            ? iconSizeNumber * countOfSteps + gapBetweenText * countOfSteps - gapBetweenIcons
            : // Если нет стрелочки, то добавляется еще одна иконка и отступ для иконок
              iconSizeNumber * (countOfSteps + 1) + gapBetweenText * countOfSteps;
    }

    const margin = countMarginLevel();

    const handleClick = useCallback(() => {
        if (isMobile()) {
            onItemClick();
            return;
        }
        onIconClick();
    }, [onIconClick, onItemClick]);

    return (
        <ListItem
            {...restProps}
            // Поиск по дереву не поддерживается, поэтому поисковое значение не используется
            textValue={null}
            help={!hasCheckbox ? help : undefined}
            isDisabled={isDisabled}
            data-testid={dataTestId}
            className={cls({ size }, [props.className])}
            addonLeft={
                <HStack gap="8" alignItems="flex-start">
                    {margin > 0 && <div style={{ marginLeft: margin }} />}
                    {hasExpandIcon && (
                        <div style={{ height: checkboxSizeToIconHeight[size] }} className={addonClasses}>
                            <ClickableIcon
                                onClick={onIconClick}
                                role="supplementary"
                                color="current"
                                data-testid={`${dataTestId}.ExpandIcon`}
                                data-expanded={isExpanded}
                            >
                                <Icon size={iconSize} />
                            </ClickableIcon>
                        </div>
                    )}
                    {hasCheckbox && (
                        <VStack className={cls('checkbox', [addonClasses])}>
                            <Checkbox
                                size={checkboxSizeMap[size]}
                                isSelected={isSelected}
                                isDisabled={isDisabled}
                                isIndeterminate={isIndeterminate}
                                data-testid={`${dataTestId}.Checkbox`}
                                onChange={onItemClick}
                                isTruncated={isTruncated}
                                help={help}
                                caption={
                                    <TextGenericWithSkin
                                        data-testid={`${dataTestId}.Checkbox.Text`}
                                        className={cls('content')}
                                        textSize={textSize}
                                        skinSize={size}
                                        caption={caption}
                                        color="current"
                                        isTruncated={isTruncated}
                                    />
                                }
                            >
                                {children}
                            </Checkbox>
                        </VStack>
                    )}
                </HStack>
            }
            children={!hasCheckbox && children}
            addonRight={props.addonRight}
            onClick={hasCheckbox ? handleClick : undefined}
        />
    );
};
